import React, { useState, useEffect } from 'react';
import Card from '../../../components/ui/Card';
import Button from '../../../components/ui/Button';
import Table from '../../../components/ui/Table';
import Modal from '../../../components/ui/Modal';
import Select from '../../../components/ui/Select';
import Input from '../../../components/ui/Input';
import Badge from '../../../components/ui/Badge';
import Loading from '../../../components/ui/Loading';
import Alert from '../../../components/ui/Alert';
import Textarea from '../../../components/ui/Textarea';
import { Plus, MapPin, User, Trash2, Search, Mail, Shield, Star } from 'lucide-react';
import { userManagementService, locationService } from '../../../services';
import type { LocationAdmin, UserManagement, Location, LocationAdminAssignmentRequest } from '../../../types';

const LocationAssignmentsTab: React.FC = () => {
  const [locations, setLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [admins, setAdmins] = useState<LocationAdmin[]>([]);
  const [users, setUsers] = useState<UserManagement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState<LocationAdmin | null>(null);
  
  const [assignmentData, setAssignmentData] = useState<Partial<LocationAdminAssignmentRequest>>({
    user_profile_id: undefined,
    location_id: undefined,
    is_primary_admin: false,
    notes: '',
  });

  useEffect(() => {
    fetchLocations();
    fetchLocationAdminUsers();
  }, []);

  useEffect(() => {
    if (selectedLocation) {
      fetchLocationAdmins(selectedLocation.ycl_id);
      setAssignmentData((prev) => ({ ...prev, location_id: selectedLocation.ycl_id }));
    }
  }, [selectedLocation]);

  const fetchLocations = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await locationService.getLocations();
      const locationData = response.data || [];
      setLocations(locationData);
      
      // Auto-select first location
      if (locationData && locationData.length > 0) {
        setSelectedLocation(locationData[0]);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch locations');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchLocationAdminUsers = async () => {
    try {
      // Fetch users with Location Admin role (role_id 1052)
      const response = await userManagementService.getUsers({
        role_id: 1052,
        is_active: true,
        include_child_companies: true,
      });
      setUsers(response.users || []);
    } catch (err) {
      console.error('Failed to fetch location admin users:', err);
    }
  };

  const fetchLocationAdmins = async (locationId: number) => {
    try {
      setError(null);
      const response = await userManagementService.getLocationAdmins(locationId);
      setAdmins(response.admins || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch location admins');
    }
  };

  const handleAssignAdmin = async () => {
    if (!assignmentData.user_profile_id || !assignmentData.location_id) {
      setError('Please select a user');
      return;
    }

    try {
      setIsAssigning(true);
      setError(null);
      
      await userManagementService.assignAdminToLocation(
        assignmentData.location_id,
        assignmentData as LocationAdminAssignmentRequest
      );
      
      setSuccess('Location admin assigned successfully');
      setIsAssignModalOpen(false);
      resetAssignmentForm();
      
      if (selectedLocation) {
        fetchLocationAdmins(selectedLocation.ycl_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to assign location admin');
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRemoveAdmin = async () => {
    if (!selectedAdmin || !selectedLocation) return;

    try {
      setError(null);
      await userManagementService.removeAdminFromLocation(
        selectedLocation.ycl_id,
        selectedAdmin.user_profile_id
      );
      
      setSuccess('Location admin removed successfully');
      setIsRemoveModalOpen(false);
      setSelectedAdmin(null);
      
      if (selectedLocation) {
        fetchLocationAdmins(selectedLocation.ycl_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove location admin');
    }
  };

  const openRemoveModal = (admin: LocationAdmin) => {
    setSelectedAdmin(admin);
    setIsRemoveModalOpen(true);
  };

  const resetAssignmentForm = () => {
    setAssignmentData({
      user_profile_id: undefined,
      location_id: selectedLocation?.ycl_id,
      is_primary_admin: false,
      notes: '',
    });
  };

  const filteredLocations = locations.filter((loc) =>
    loc.ycl_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (loc.ycl_code?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const columns = [
    {
      key: 'admin',
      label: 'Admin User',
      render: (admin: LocationAdmin) => (
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-accent-primary/10 rounded-full flex items-center justify-center">
            <User size={18} className="text-accent-primary" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-text-primary">
                {admin.user_first_name} {admin.user_last_name}
              </span>
              {admin.is_primary_admin && (
                <Star size={14} className="text-accent-warning fill-accent-warning" />
              )}
            </div>
            <div className="flex items-center gap-1 text-xs text-text-muted">
              <Mail size={12} />
              {admin.user_email}
            </div>
          </div>
        </div>
      ),
    },
    {
      key: 'role',
      label: 'Role',
      render: (admin: LocationAdmin) => (
        <div className="flex items-center gap-2">
          <Shield size={14} className="text-text-muted" />
          <span className="text-text-secondary">{admin.user_role_name || 'N/A'}</span>
        </div>
      ),
    },
    {
      key: 'assigned_date',
      label: 'Assigned Date',
      render: (admin: LocationAdmin) => (
        <span className="text-text-secondary text-sm">
          {new Date(admin.assigned_date).toLocaleDateString()}
        </span>
      ),
    },
    {
      key: 'assigned_by',
      label: 'Assigned By',
      render: (admin: LocationAdmin) => (
        <span className="text-text-secondary">{admin.assigned_by_name || 'System'}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (admin: LocationAdmin) => (
        <Badge variant={admin.is_active ? 'success' : 'danger'}>
          {admin.is_active ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (admin: LocationAdmin) => (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => openRemoveModal(admin)}
          className="text-status-danger hover:bg-status-danger/10"
          title="Remove"
        >
          <Trash2 size={16} />
        </Button>
      ),
    },
  ];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loading size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {(error || success) && (
        <>
          {error && <Alert variant="error" message={error} onClose={() => setError(null)} />}
          {success && <Alert variant="success" message={success} onClose={() => setSuccess(null)} />}
        </>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-text-primary">Location Admin Assignments</h2>
          <p className="text-text-muted text-sm mt-1">Assign location admins to manage specific locations</p>
        </div>
        <Button onClick={() => setIsAssignModalOpen(true)} disabled={!selectedLocation}>
          <Plus size={18} className="mr-2" />
          Assign Admin
        </Button>
      </div>

      <div className="grid grid-cols-12 gap-6">
        {/* Locations List */}
        <div className="col-span-12 lg:col-span-3">
          <Card>
            <div className="p-4 border-b border-border-primary">
              <h3 className="font-semibold text-text-primary flex items-center gap-2 mb-3">
                <MapPin size={18} />
                Locations
              </h3>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-muted" size={16} />
                <Input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 text-sm"
                  fullWidth
                />
              </div>
            </div>
            <div className="divide-y divide-border-primary max-h-[600px] overflow-y-auto">
              {filteredLocations.map((location) => (
                <button
                  key={location.ycl_id}
                  onClick={() => setSelectedLocation(location)}
                  className={`w-full p-3 text-left transition-colors ${
                    selectedLocation?.ycl_id === location.ycl_id
                      ? 'bg-accent-primary/10 border-l-4 border-accent-primary'
                      : 'hover:bg-surface-secondary'
                  }`}
                >
                  <div className="font-medium text-text-primary text-sm">{location.ycl_name}</div>
                  <div className="text-xs text-text-muted mt-1">{location.ycl_code}</div>
                  {location.ycl_city && (
                    <div className="text-xs text-text-muted">{location.ycl_city}</div>
                  )}
                </button>
              ))}
            </div>
          </Card>
        </div>

        {/* Admins List */}
        <div className="col-span-12 lg:col-span-9">
          {selectedLocation ? (
            <Card>
              <div className="p-4 border-b border-border-primary">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-text-primary">{selectedLocation.ycl_name}</h3>
                    <p className="text-sm text-text-muted mt-1">
                      {admins.length} admin{admins.length !== 1 ? 's' : ''} assigned
                    </p>
                  </div>
                  <Badge variant="default">{selectedLocation.ycl_code}</Badge>
                </div>
              </div>
              {admins.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-12 text-center">
                  <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mb-4">
                    <User size={32} className="text-text-muted" />
                  </div>
                  <h3 className="text-lg font-medium text-text-primary mb-2">No admins assigned</h3>
                  <p className="text-text-muted mb-4">
                    Assign location admins to manage this location
                  </p>
                  <Button onClick={() => setIsAssignModalOpen(true)}>
                    <Plus size={18} className="mr-2" />
                    Assign Admin
                  </Button>
                </div>
              ) : (
                <Table columns={columns} data={admins} />
              )}
            </Card>
          ) : (
            <Card>
              <div className="flex flex-col items-center justify-center p-12 text-center">
                <div className="w-16 h-16 bg-surface-secondary rounded-full flex items-center justify-center mb-4">
                  <MapPin size={32} className="text-text-muted" />
                </div>
                <h3 className="text-lg font-medium text-text-primary mb-2">Select a location</h3>
                <p className="text-text-muted">
                  Choose a location from the list to view and manage its admins
                </p>
              </div>
            </Card>
          )}
        </div>
      </div>

      {/* Assign Admin Modal */}
      <Modal
        isOpen={isAssignModalOpen}
        onClose={() => {
          setIsAssignModalOpen(false);
          resetAssignmentForm();
        }}
        title={`Assign Admin to ${selectedLocation?.ycl_name}`}
      >
        <div className="space-y-4">
          <Select
            label="Select User *"
            value={assignmentData.user_profile_id?.toString() || ''}
            onChange={(e) =>
              setAssignmentData({ ...assignmentData, user_profile_id: parseInt(e.target.value) })
            }
            required
          >
            <option value="">Select a location admin user</option>
            {users.map((user) => (
              <option key={user.profile_id} value={user.profile_id}>
                {user.first_name} {user.last_name} ({user.email})
              </option>
            ))}
          </Select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={assignmentData.is_primary_admin}
              onChange={(e) =>
                setAssignmentData({ ...assignmentData, is_primary_admin: e.target.checked })
              }
              className="w-4 h-4 rounded border-gray-300 text-accent-primary focus:ring-accent-primary"
            />
            <span className="text-sm text-text-secondary flex items-center gap-1">
              <Star size={14} className="text-accent-warning" />
              Primary Admin
            </span>
          </label>
          <Textarea
            label="Notes (Optional)"
            value={assignmentData.notes || ''}
            onChange={(e) => setAssignmentData({ ...assignmentData, notes: e.target.value })}
            placeholder="Add any notes about this assignment..."
            rows={3}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsAssignModalOpen(false);
                resetAssignmentForm();
              }}
              disabled={isAssigning}
            >
              Cancel
            </Button>
            <Button onClick={handleAssignAdmin} disabled={isAssigning}>
              {isAssigning ? (
                <>
                  <Loading size="sm" className="mr-2" />
                  Assigning...
                </>
              ) : (
                'Assign Admin'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Remove Confirmation Modal */}
      <Modal
        isOpen={isRemoveModalOpen}
        onClose={() => {
          setIsRemoveModalOpen(false);
          setSelectedAdmin(null);
        }}
        title="Remove Location Admin"
      >
        <div className="space-y-4">
          <p className="text-text-secondary">
            Are you sure you want to remove{' '}
            <strong>
              {selectedAdmin?.user_first_name} {selectedAdmin?.user_last_name}
            </strong>{' '}
            as an admin for <strong>{selectedLocation?.ycl_name}</strong>?
          </p>
          <div className="flex justify-end gap-3 pt-4">
            <Button
              variant="secondary"
              onClick={() => {
                setIsRemoveModalOpen(false);
                setSelectedAdmin(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleRemoveAdmin}>
              Remove
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default LocationAssignmentsTab;
